<template>
  <div class="max-w-prose space-y-3">
    <ReturnComponent :to="{ name: 'messages-contact' }">
      Terug naar contactopties
    </ReturnComponent>
    <TextTitle>Algemene vraag</TextTitle>
    <MessageComponent v-if="submitted">
      We hebben je bericht ontvangen. We nemen binnen 5 werkdagen per e-mail
      contact met je op.
    </MessageComponent>
    <template v-else>
      <p>
        Heb je een vraag? Vul het formulier zo volledig mogelijk in en we nemen
        binnen 5 werkdagen per e-mail contact met je op.
      </p>
      <p>Velden met een <span class="text-danger">*</span> zijn verplicht.</p>
      <BaseCard inner class="space-y-2 p-4">
        <ValidatedFormComponent
          ref="contactForm"
          novalidate
          class="space-y-4"
          @submit.prevent="onSubmit"
        >
          <ValidatedInputComponent
            v-model="subject"
            :input-component="InputField"
            :validator="validators.subject"
            required
            label="Onderwerp"
          />
          <div class="space-y-2">
            <MeteringPointPickerComponent
              v-model="meteringPoint"
              label="Connectie of installatie"
              helper-text="Selecteer eventueel een connectie of installatie als de vraag daar specifiek over gaat."
              class="w-full"
            />
          </div>
          <ValidatedInputComponent
            v-model="message"
            :input-component="InputArea"
            :validator="validators.message"
            required
            label="Bericht"
          />
          <BaseButton
            type="submit"
            color="primary"
            :loading="submitting"
            class="w-fit"
          >
            Verstuur
          </BaseButton>
        </ValidatedFormComponent>
      </BaseCard>
    </template>
  </div>
</template>

<script setup>
import { ref } from "vue"
import HTTP_STATUS from "@/helpers/httpStatus.js"
import { triggerEvent } from "@/services/plausibleService.js"
import useNotificationStore from "@/stores/notificationStore.js"
import useFilterStore from "@/stores/filterStore.js"
import useUserStore from "@/stores/userStore.js"
import useContactStore from "@/stores/repower/contactStore.js"
import {
  BaseButton,
  BaseCard,
  InputField,
  InputArea,
  TextTitle,
} from "@repowerednl/ui-component-library"
import ReturnComponent from "@/components/navigation/ReturnComponent.vue"
import MeteringPointPickerComponent from "@/components/input/MeteringPointPickerComponent.vue"
import ValidatedInputComponent from "@/components/input/ValidatedInputComponent.vue"
import ValidatedFormComponent from "@/components/input/ValidatedFormComponent.vue"
import MessageComponent from "@/components/information/MessageComponent.vue"

const MIN_MESSAGE_LENGTH = 20

const notificationStore = useNotificationStore()
const userStore = useUserStore()
const subject = ref("")
const meteringPoint = ref()
const message = ref("")
const contactForm = ref()
const submitting = ref(false)
const submitted = ref(false)

const validators = {
  subject(value) {
    return value?.length > 0
      ? { valid: true }
      : { valid: false, message: "Vul een onderwerp in" }
  },
  message(value) {
    return value?.length >= MIN_MESSAGE_LENGTH
      ? { valid: true }
      : {
          valid: false,
          message: `Gebruik minstens ${MIN_MESSAGE_LENGTH} tekens`,
        }
  },
}

function onError(error) {
  submitting.value = false
  if (error.code === HTTP_STATUS.BAD_REQUEST) {
    contactForm.value.validate()
    notificationStore.pushError(
      "Kan formulier niet versturen",
      "Sommige verplichte velden zijn niet ingevuld.",
      "form-invalid",
    )
  } else {
    notificationStore.pushError(
      "Kan formulier niet versturen",
      `Er is een fout opgetreden. Probeer het later opnieuw. (code: ${error.code})`,
      "form-error",
    )
  }
}

async function onSubmit() {
  if (!contactForm.value.validate()) {
    triggerEvent("contact:question-form-invalid")
    return
  }
  submitting.value = true
  const data = {
    user_id: userStore.id,
    portfolio_id: useFilterStore().currentPortfolio.id,
    subject: subject.value,
    message: message.value,
    metering_point_id: meteringPoint.value?.id,
  }
  useContactStore().submitForm(
    "general",
    data,
    () => {
      submitting.value = false
      submitted.value = true
      triggerEvent("contact:question-form-submitted")
    },
    onError,
  )
}
</script>
